import * as React from "react";

import { useNotification } from "@/shared/components/notifications/useNotification";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

/**
 * Click handler that copies ``url`` to the clipboard and confirms with a
 * toast: "Link copied", or an error toast when the browser refuses the write.
 * It stops the click from reaching clickable ancestors, resolves either way
 * rather than rejecting, and calls ``onCopied`` with the outcome once the
 * toast has been dispatched.
 */
export function useCopyLink(
  url: string,
  onCopied?: (ok: boolean) => void,
): (e: React.MouseEvent) => Promise<void> {
  const showNotification = useNotification();

  return React.useCallback(
    async (e: React.MouseEvent) => {
      // A MUI TableRow with its own onClick would otherwise open the row when
      // the user only meant to copy its link.
      e.stopPropagation();
      let ok: boolean;
      try {
        await navigator.clipboard.writeText(url);
        ok = true;
      } catch (err) {
        // A refused write rejects with a NotAllowedError, and an insecure
        // origin leaves navigator.clipboard undefined so the call above throws
        // a TypeError. Both are reported and swallowed, never re-thrown: the
        // caller gets a resolved promise, and the user gets the error toast
        // below instead of a click that appears to do nothing.
        ErrorMonitoringService.captureError(err, {
          context: { source: "useCopyLink" },
        });
        ok = false;
      }
      if (ok) {
        showNotification("Link copied");
      } else {
        showNotification("Couldn't copy link", "error");
      }
      onCopied?.(ok);
    },
    [url, onCopied, showNotification],
  );
}
